/**
 * 🟦 V18.2 — LyonGenieIcon.
 *
 * Componente reusable del "Geniecito" oficial de DamyxIA: cabeza con
 * turbante + nudo dorado + cola de humo en espiral + burbujitas
 * decorativas animadas alrededor.
 *
 * El SVG fue copiado del original que vive inline en LyonHeaderButton.jsx
 * (líneas 69-116) para mantener una sola fuente de diseño y permitir
 * reusarlo en cards, paneles, etc. sin duplicar markup.
 *
 * Props:
 *  - size  : tamaño del SVG del genio (default 28).
 *  - bubbles: si renderizar burbujitas animadas (default true).
 *  - className: clases extra para el wrapper.
 */
const LyonGenieIcon = ({ size = 28, bubbles = true, className = '' }) => (
  <div
    className={`relative inline-flex items-center justify-center ${className}`}
    style={{ width: size + 14, height: size + 14 }}
    data-testid="lyon-genie-icon"
  >
    {/* SVG del Geniecito (idéntico al de LyonHeaderButton.jsx) */}
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      className="relative z-10 drop-shadow-[0_0_8px_rgba(167,139,250,0.5)]"
    >
      <defs>
        <linearGradient id={`lyonGenieGrad-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#A5F3FC" />
          <stop offset="55%" stopColor="#C4B5FD" />
          <stop offset="100%" stopColor="#F0ABFC" />
        </linearGradient>
      </defs>
      {/* Cola de humo en espiral debajo del genio */}
      <path
        d="M16 28 C 11 26.5, 21 24, 16 22.5 C 11 21, 21 19, 16 17.5"
        stroke={`url(#lyonGenieGrad-${size})`}
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
        opacity="0.75"
      />
      {/* Cuerpo (forma de humo redondeado) */}
      <path
        d="M10 16.5 C 10 13, 13 11, 16 11 C 19 11, 22 13, 22 16.5 L 22 17 C 22 17.6, 21.4 18, 20.8 18 L 11.2 18 C 10.6 18, 10 17.6, 10 17 Z"
        fill={`url(#lyonGenieGrad-${size})`}
        opacity="0.95"
      />
      {/* Turbante */}
      <ellipse cx="16" cy="8.5" rx="5.5" ry="3" fill={`url(#lyonGenieGrad-${size})`} />
      {/* Nudo del turbante */}
      <circle cx="16" cy="5.5" r="1.6" fill="#FDE68A" />
      {/* Ojos */}
      <circle cx="14" cy="14.5" r="0.9" fill="#0F172A" />
      <circle cx="18" cy="14.5" r="0.9" fill="#0F172A" />
      {/* Sonrisa */}
      <path
        d="M14.4 16.2 Q 16 17.3, 17.6 16.2"
        stroke="#0F172A"
        strokeWidth="0.9"
        strokeLinecap="round"
        fill="none"
      />
    </svg>

    {/* Burbujitas decorativas pulsando alrededor */}
    {bubbles && (
      <>
        <span
          className="absolute rounded-full bg-cyan-300/80 blur-[1px] animate-pulse"
          style={{ width: 7, height: 7, top: '8%', left: '-6%', animationDelay: '0s' }}
          aria-hidden="true"
        />
        <span
          className="absolute rounded-full bg-fuchsia-300/80 blur-[1px] animate-pulse"
          style={{ width: 6, height: 6, top: '20%', right: '-8%', animationDelay: '0.5s' }}
          aria-hidden="true"
        />
        <span
          className="absolute rounded-full bg-purple-300/80 blur-[1px] animate-pulse"
          style={{ width: 5, height: 5, bottom: '8%', left: '-2%', animationDelay: '1s' }}
          aria-hidden="true"
        />
        <span
          className="absolute rounded-full bg-violet-300/80 blur-[0.5px] animate-pulse"
          style={{ width: 4, height: 4, bottom: '14%', right: '-4%', animationDelay: '1.4s' }}
          aria-hidden="true"
        />
      </>
    )}
  </div>
);

export default LyonGenieIcon;
